import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Titulo from '@components/Titulo';
import Resumen from '@components/Resumen';
import { cajaPrincipalStyle } from '@styles/cajaPrincipal';
import { cajaContenedorStyle } from '@styles/cajaContenedor';
import { cajaContenedorPrimeroStyle } from '@styles/cajaContenedorPrimero';
import Boton from '@components/Boton';
import { colors } from '@styles/colors';
import LoadingOverlay from '@components/LoadingOverlay';

import { verDispApi } from "@api/wp/agenda/disponibilidad/disponibilidadApi"
import { verCitaExtraApi } from "@api/wp/agenda/citasextras/citasextrasApi";
import { checkDuplicateApi } from "@api/wp/agenda/duplicate/duplicateApi";
import { useCita } from '@providers/citaContext';

function Fechas() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cita, setCita } = useCita();
  const ciudadId = cita?.sedeId ? cita?.ciudadId : (location.state?.ciudadId);
  const ciudadNombre = cita?.sedeId ? cita?.ciudadNombre : (location.state?.ciudadNombre);
  const empresaNombre = cita?.empresaArray ?? location.state?.empresaNombre;
  const sedeId = cita?.sedeId ?? location.state?.sedeId;
  const sedeIdOficina = cita?.sedeIdOficina ?? location.state?.sedeIdOficina;
  const especialidadNombre = cita?.especialidadNombre ?? location.state?.especialidadNombre;
  const empresaSeleccionada = cita?.empresaSeleccionada ?? location.state?.empresaSeleccionada;
  const oidEspecialidad = cita?.oidEspecialidad ?? location.state?.oidEspecialidad;
  const sedeNombre = cita?.sedeNombre ?? location.state?.sedeNombre;
  const valorPreviplan = cita?.valorPreviplan ?? location.state?.valorPreviplan;
  const sku = cita?.sku ?? location.state?.sku;
  const nombreServicio = cita?.nombreServicio ?? location.state?.nombreServicio;
  const descripcion = (cita?.descripcion ?? location.state?.descripcion) || '';

  const [disponibilidad, setDisponibilidad] = useState([]);
  const [cargandoDisponibilidad, setCargandoDisponibilidad] = useState(false);
  const [errorDisponibilidad, setErrorDisponibilidad] = useState('');

  // Estados para el calendario dinámico
  const [mesActual, setMesActual] = useState(new Date());
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [citaDuplicada, setCitaDuplicada] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Validar que el contexto de cita no esté vacío; si lo está, regresar a Home
  useEffect(() => {
    const citaVacia = !cita || (typeof cita === 'object' && Object.keys(cita).length === 0);
    if (citaVacia) {
      navigate('/home');
      return;
    }
  }, []);

  useEffect(() => {
    verDisponibilidadPorEspecialidades();
  }, [ciudadId, sedeId, oidEspecialidad]);

  // Funciones para el calendario
  const obtenerDiasDelMes = (fecha) => {
    const año = fecha.getFullYear();
    const mes = fecha.getMonth();
    const primerDia = new Date(año, mes, 1);
    const ultimoDia = new Date(año, mes + 1, 0);
    const diasEnMes = ultimoDia.getDate();
    const primerDiaSemana = primerDia.getDay();

    const dias = [];

    // Agregar días vacíos al inicio
    for (let i = 0; i < primerDiaSemana; i++) {
      dias.push(null);
    }

    // Agregar días del mes
    for (let dia = 1; dia <= diasEnMes; dia++) {
      dias.push(dia);
    }

    return dias;
  };

  const formatearFecha = (año, mes, dia) => {
    return `${año}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  };

  const esFechaDisponible = (año, mes, dia) => {
    const fechaFormateada = formatearFecha(año, mes, dia);
    const disponible = disponibilidad.includes(fechaFormateada);
    //console.log(`¿Fecha ${fechaFormateada} disponible?`, disponible);
    return disponible;
  };

  const cambiarMes = (direccion) => {
    const nuevaFecha = new Date(mesActual);
    nuevaFecha.setMonth(mesActual.getMonth() + direccion);
    setMesActual(nuevaFecha);
  };

  const nombresMeses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const nombresDias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const verDisponibilidadPorEspecialidades = async () => {
    setCargandoDisponibilidad(true);
    setErrorDisponibilidad('');
    setDisponibilidad([]);

    try {
      const requ = {
        "empresa": empresaSeleccionada,
        "oid_sede": sedeId,
        "oid_especialidad": oidEspecialidad
      };
      console.log('Request fechas:', requ);
      const response = await verDispApi(requ);
      console.log('respose:', response);
      // Agregar las disponibilidades al array total, evitando duplicados por fecha y especialidad
      if (response && response.data) {
        setDisponibilidad(response.data);
        console.log('Estado fechasDisponibles después:', response.data);
      }

    } catch (error) {
      console.error('Error al consultar disponibilidad:', error);
      setErrorDisponibilidad('Error al cargar la disponibilidad');
    } finally {
      setCargandoDisponibilidad(false);
    }
  };

  const manejarSeleccionFecha = async (año, mes, dia) => {
    if (esFechaDisponible(año, mes, dia)) {
      const fechaFormateada = formatearFecha(año, mes, dia);
      setFechaSeleccionada(fechaFormateada);
      setCitaDuplicada(false);
      setLoadingCheck(true);

      try {
        const id_paciente = localStorage.getItem('idUsu');
        const response = await checkDuplicateApi({
          id_paciente: id_paciente,
          oid_especialidad: oidEspecialidad,
          fecha: fechaFormateada
        });

        if (response && response.result && response.result.exists) {
          setCitaDuplicada(true);
        }
      } catch (error) {
        console.error('Error checking duplicate:', error);
      } finally {
        setLoadingCheck(false);
      }

      console.log('Fecha seleccionada:', fechaFormateada);
    }
  };

  const continuarFechas = () => {
    if (!fechaSeleccionada || citaDuplicada || loadingCheck) return;
    setCita(prev => ({
      ...prev,
      ciudadId,
      ciudadNombre,
      especialidadNombre,
      nombreServicio,
      descripcion,
      oidEspecialidad,
      empresaSeleccionada,
      sedeId,
      sedeIdOficina,
      sedeNombre,
      fecha: fechaSeleccionada,
      empresaArray: empresaNombre,
      valorPreviplan,
      sku,
    }));
    navigate('/agenda/medicos');
  };

  const verCitaExtra = async () => {
    try {
      const requ = {
        "oid_especialidad": oidEspecialidad
      };
      const response = await verCitaExtraApi(requ);
      // Agregar las disponibilidades al array total, evitando duplicados por fecha y especialidad
      if (response && response.result) {
        setCitasExtras(response.result[0].cantidad)
      }
    } catch (error) {
      console.error('Error al consultar disponibilidad:', error);
      //setErrorDisponibilidad('Error al cargar la disponibilidad');
    } finally {
    }
  };

  return (
    <div id="cajaPrincipal"
      style={cajaPrincipalStyle}
    >
      <LoadingOverlay visible={cargandoDisponibilidad} />
      <Titulo titulo="Selecciona la fecha" subtitulo="Elige un día disponible en el calendario para programar tu cita."
        imagen_titulo="fecha"
        rutaRetorno="/ubicacion"
        stateRetorno={{
          ciudadId: ciudadId,
          ciudadNombre: ciudadNombre,
          especialidadNombre: especialidadNombre,
          oidEspecialidad: oidEspecialidad,
          empresaSeleccionada: empresaSeleccionada,
          sedeId: sedeId,
          sedeIdOficina: sedeIdOficina,
          sedeNombre: sedeNombre,
          empresaNombre
        }}
      ></Titulo>

      {/* Div central */}
      <div style={{...cajaContenedorStyle, ...(isMobile && { marginLeft: '0', width: '100%', padding: '0 2vh', boxSizing: 'border-box' })}}>
        {/* Primer div */}

        {/* Contenido del primer div */}
        <div style={{...cajaContenedorPrimeroStyle, ...(isMobile && { width: '100%', marginLeft: '0', display: 'flex', flexDirection: 'column', alignItems: 'center' })}}>
          {/* Calendario dinámico */}
          <div style={{
            marginTop: '3vh',
            marginLeft: isMobile ? '0' : '6vh',
            backgroundColor: colors.white,
            borderRadius: '2vh',
            alignItems: 'center',            
            padding: isMobile ? '2vh 1vh' : '2vh',
            boxShadow: `0 0.2vh 1vh ${colors.tableShadow}`,
            width: isMobile ? '100%' : '100%',
            maxWidth: isMobile ? '100%' : '50vh',
            boxSizing: 'border-box'
          }}>
            {/* Header del calendario */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vh',
              padding: '0 1vh'
            }}>
              <button
                onClick={() => cambiarMes(-1)}
                style={{
                  backgroundColor: colors.primary,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '50%',
                  width: '4vh',
                  height: '4vh',
                  fontSize: '1.8vh',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ←
              </button>

              <h3 style={{
                margin: 0,
                fontSize: '2.2vh',
                fontFamily: 'Inter',
                fontWeight: '600',
                color: colors.gray700
              }}>
                {nombresMeses[mesActual.getMonth()]} {mesActual.getFullYear()}
              </h3>

              <button
                onClick={() => cambiarMes(1)}
                style={{
                  backgroundColor: colors.primary,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '50%',
                  width: '4vh',
                  height: '4vh',
                  fontSize: '1.8vh',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                →
              </button>
            </div>

            {/* Días de la semana */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.5vh',
              marginBottom: '1vh'
            }}>
              {nombresDias.map(dia => (
                <div key={dia} style={{
                  textAlign: 'center',
                  fontSize: '1.4vh',
                  fontFamily: 'Inter',
                  fontWeight: '600',
                  color: colors.gray600,
                  padding: '1vh 0'
                }}>
                  {dia}
                </div>
              ))}
            </div>

            {/* Días del mes */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '0.5vh'
            }}>
              {obtenerDiasDelMes(mesActual).map((dia, index) => {
                const esDisponible = dia && esFechaDisponible(
                  mesActual.getFullYear(),
                  mesActual.getMonth(),
                  dia
                );
                const fechaCelda = dia ? formatearFecha(mesActual.getFullYear(), mesActual.getMonth(), dia) : null;
                const seleccionada = fechaSeleccionada === fechaCelda;

                return (
                  <div
                    key={index}
                    style={{
                      height: '5vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.6vh',
                      fontFamily: 'Inter',
                      borderRadius: '0.5vh',
                      cursor: dia && esDisponible ? 'pointer' : 'default',
                      backgroundColor: dia ? (seleccionada ? colors.accent : esDisponible ? colors.verdeClaro : colors.gray100) : 'transparent',
                      color: dia ? (seleccionada ? colors.white : esDisponible ? colors.primaryText : colors.gray600) : 'transparent',
                      fontWeight: (seleccionada || esDisponible) ? '600' : '400',
                      transition: 'all 0.2s ease',
                      border: dia ? (seleccionada ? `2px solid ${colors.primaryText}` : esDisponible ? `2px solid ${colors.verdeClaro}` : 'none') : 'none',
                      visibility: dia ? 'visible' : 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (dia && esDisponible && !seleccionada) {
                        e.target.style.backgroundColor = colors.accent;
                        e.target.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (dia && esDisponible && !seleccionada) {
                        e.target.style.backgroundColor = colors.verdeClaro;
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                    onClick={() => {
                      if (dia && esDisponible) {
                        manejarSeleccionFecha(mesActual.getFullYear(), mesActual.getMonth(), dia);
                      }
                    }}
                  >
                    {dia}
                  </div>
                );
              })}
            </div>

            {/* Leyenda */}
            <div style={{
              marginTop: '2vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2vh'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5vh'
              }}>
                <div style={{
                  width: '2vh',
                  height: '2vh',
                  backgroundColor: colors.verdeClaro,
                  borderRadius: '0.3vh'
                }}></div>
                <span style={{
                  fontSize: '1.4vh',
                  fontFamily: 'Inter',
                  color: colors.gray600
                }}>
                  Fechas disponibles
                </span>
              </div>
            </div>
          </div>
          <div id="cajaSiguiente" style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: isMobile ? '100%' : '80vh',
            marginTop: '1vh',
            gap: '2vh',
            justifyContent: isMobile ? 'center' : 'flex-end',
            alignItems: 'center',
            position: 'relative',            
          }}>
            {citaDuplicada && (
              <div style={{
                              color: colors.warnIcon,
                              fontWeight: '600',
                              fontSize: '2vh',
                              backgroundColor: colors.warnBg,
                              padding: '1vh 2vh',
                              borderRadius: '1vh',
                              border: `1px solid ${colors.warnIcon}`
              }}>
                Ya tiene una cita de esta especialidad para este día
              </div>
            )}
            <Boton
              label={loadingCheck ? "Verificando..." : "Continuar"}
              onClick={continuarFechas}
              siguiente
              ancho={isMobile ? '100%' : '23vh'}
              alto="5.2vh"
              disabled={!fechaSeleccionada || citaDuplicada || loadingCheck}
            />
          </div>          
        </div>

        {/* Contenedor derecho para el resumen */}
        {!isMobile && <Resumen ciudad={ciudadNombre} servicio={especialidadNombre} ubicacion={sedeNombre}  nomServicio={nombreServicio} descripcion={descripcion} />}
      </div>
    </div>
  );
}

export default Fechas;
